import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DrawerRight from 'Components/DrawerRight'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import SPMActivityDialog, {
  type ActivityData
} from 'Components/SPMActivityDialog/SPMActivityDialog'
import SPMActivityDialogButton from 'Components/SPMActivityDialogButton'
import SPMAllowedApiClients from 'Components/SPMAllowedApiClients'
import { WithLoader } from 'Components/WithLoader'
import DeviceDetails from 'Services/ServiceProviderManagement/Features/Device/Components/DeviceDetails'
import DeviceList from 'Services/ServiceProviderManagement/Features/Device/Components/DeviceList/DeviceList'
import {
  type Device,
  DeviceTabType,
  fetchDevices,
  fetchSingleDevice,
  updateSingleDevice
} from 'Services/ServiceProviderManagement/Features/Device/deviceSlice'
import {
  useDevicesWithParams,
  useSingleDevice
} from 'Services/ServiceProviderManagement/Features/Device/Hooks'
import { SELECTED_DEVICE_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/Device/Hooks/useDevicesWithParams'
import { useAppDispatch } from 'Store'
import {
  ROWS_PER_PAGE_DEFAULT,
  SERVICE_PROVIDER_MANAGEMENT_ROOT_URL,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from 'Util/constants'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Button, Divider, Tab, Typography } from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

const DEVICE_DETAILS_DRAWER_TAB_PARAMETER_KEY = 'deviceDetailsDrawerTab'

export default function DeviceListPage() {
  const baseUrl = `${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/devices`
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const {
    devices,
    loading,
    total_results,
    params,
    setParams,
    selectedDeviceId,
    setSelectedDeviceId
  } = useDevicesWithParams()

  const [searchText, setSearchText] = useState(params.name ?? '')
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    singleDevice,
    singleLoading: singleDeviceLoading,
    submitLoading
  } = useSingleDevice(undefined)
  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedDeviceId)
  const [activityDialogOpen, setActivityDialogOpen] = useState(false)
  const deviceDetailsDrawerTabFromParams =
    searchParams.get(DEVICE_DETAILS_DRAWER_TAB_PARAMETER_KEY) ?? DeviceTabType.DETAILS

  const [selectedReadClients, setSelectedReadClients] = useState<string[]>(
    singleDevice?.client_read ?? []
  )
  const [selectedWriteClients, setSelectedWriteClients] = useState<string[]>(
    singleDevice?.client_write ?? []
  )

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SERVICE_PROVIDER_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Devices`
    }
  ]

  function handleRequestSort(_event: React.MouseEvent<unknown, MouseEvent>, property: string) {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({
      ...prevState,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }))
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleSearch = () => {
    setParams(prevState => ({
      ...prevState,
      search_text: searchText,
      start_index: undefined
    }))
  }

  const handleDrawerClose = () => {
    setSelectedDeviceId('')
    setIsDrawerOpen(false)
    searchParams.delete(DEVICE_DETAILS_DRAWER_TAB_PARAMETER_KEY)
    setSearchParams(searchParams)
  }

  const handleRowClick = (id: string) => {
    setSelectedDeviceId(id)
    setIsDrawerOpen(true)
  }

  const handleActivityDialogOpen = () => {
    setActivityDialogOpen(true)
  }

  const handleActivityDialogClose = () => {
    setActivityDialogOpen(false)
  }

  const handleActivityDialogSubmit = (
    e: React.FormEvent<HTMLFormElement>,
    activityData: ActivityData
  ) => {
    e.preventDefault()
    const deviceData = {
      ...singleDevice,
      ...activityData
    } as Device

    dispatch(
      updateSingleDevice({
        ...deviceData,
        id: deviceData.device_id
      })
    )
      .then(unwrapResult)
      .then(() => {
        SnackbarUtils.success(t`Device successfully updated.`)
        dispatch(fetchDevices(params))
        dispatch(fetchSingleDevice({ id: selectedDeviceId as string }))
        handleActivityDialogClose()
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error updating the device.`)
      })
  }

  const handleAllowedApiClientsSave = () => {
    if (!singleDevice) return

    const deviceData = {
      ...singleDevice,
      client_read: selectedReadClients,
      client_write: selectedWriteClients
    }

    dispatch(
      updateSingleDevice({
        ...deviceData,
        id: singleDevice.device_id
      })
    )
      .then(unwrapResult)
      .then(() => {
        SnackbarUtils.success(t`Device successfully updated.`)
        const selectedSalesPointParam = `${SELECTED_DEVICE_PARAMETER_KEY}=${singleDevice.device_id}`
        const detailsDrawerTabParam = `${DEVICE_DETAILS_DRAWER_TAB_PARAMETER_KEY}=${DeviceTabType.ALLOWED_API_CLIENTS}`
        navigate(`${baseUrl}?${selectedSalesPointParam}&${detailsDrawerTabParam}`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error updating the device.`)
      })
  }

  const handleDeviceDetailsDrawerTabChange = (
    _event: React.SyntheticEvent,
    newValue: DeviceTabType
  ) => {
    searchParams.set(DEVICE_DETAILS_DRAWER_TAB_PARAMETER_KEY, newValue)
    setSearchParams(searchParams)
  }

  const isLoading = loading === 'pending'
  const isSingleDeviceLoading = singleDeviceLoading === 'pending'
  const isSubmitLoading = submitLoading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>{t`Devices`}</Typography>
        <Box mt={4}>
          <ListActions>
            <CustomButton
              onClick={() => navigate(`${baseUrl}/create/`)}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Create new device`}
            </CustomButton>
            <ListSearch
              searchText={searchText}
              handleSearchTextChange={handleSearchTextChange}
              handleSearch={handleSearch}
              placeholder={t`Search by name or code`}
            />
          </ListActions>
          <Box pt={1}>
            <WithLoader loading={isLoading} rows={1} height={33}>
              <DeviceList
                rows={devices}
                isLoading={isLoading}
                params={params}
                handleRequestSort={handleRequestSort}
                handleChangePage={handleChangePage}
                count={total_results}
                page={
                  params.start_index
                    ? Number.parseInt(params.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                    : 0
                }
                onRowClick={handleRowClick}
                selectedDeviceId={selectedDeviceId}
              />
            </WithLoader>
          </Box>
        </Box>
        <DrawerRight
          open={isDrawerOpen}
          onClose={handleDrawerClose}
          topBar={
            <Box>
              {singleDevice && (
                <SPMActivityDialogButton
                  activityEndDate={singleDevice.activity_end_date?.toString()}
                  isActive={singleDevice.active}
                  isLoading={isSubmitLoading}
                  onClick={handleActivityDialogOpen}
                  sx={{ mr: 1 }}
                />
              )}
              <CustomButton
                onClick={() => navigate(`${baseUrl}/${selectedDeviceId}/edit`)}
                size="small"
                startIcon={<EditIcon />}
              >{t`Edit`}</CustomButton>
            </Box>
          }
        >
          <WithLoader loading={isSingleDeviceLoading}>
            {singleDevice && (
              <TabContext value={deviceDetailsDrawerTabFromParams}>
                <TabList
                  aria-label={t`Sales point tab list`}
                  onChange={handleDeviceDetailsDrawerTabChange}
                >
                  <Tab label={t`Details`} value={DeviceTabType.DETAILS} />
                  <Tab label={t`Allowed API clients`} value={DeviceTabType.ALLOWED_API_CLIENTS} />
                </TabList>
                <Divider />
                <TabPanel sx={{ p: 0 }} value={DeviceTabType.DETAILS}>
                  <DeviceDetails device={singleDevice} />
                </TabPanel>
                <TabPanel sx={{ p: 0 }} value={DeviceTabType.ALLOWED_API_CLIENTS}>
                  <SPMAllowedApiClients
                    selectedReadClients={selectedReadClients}
                    selectedWriteClients={selectedWriteClients}
                    setSelectedReadClients={setSelectedReadClients}
                    setSelectedWriteClients={setSelectedWriteClients}
                  />
                  <Box
                    sx={{
                      mt: 2,
                      mx: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      columnGap: 2
                    }}
                  >
                    <Box display="flex" gap={2} sx={{ width: '100%' }}>
                      <Button
                        variant="outlined"
                        onClick={handleDrawerClose}
                        color="inherit"
                        sx={{ width: '100%' }}
                      >
                        {t`Cancel`}
                      </Button>
                      <CustomLoadingButton
                        onClick={handleAllowedApiClientsSave}
                        isLoading={isSubmitLoading}
                        sx={{ width: '100%' }}
                        icon={<SaveIcon />}
                        iconPosition="start"
                      >
                        {t`Save changes`}
                      </CustomLoadingButton>
                    </Box>
                  </Box>
                </TabPanel>
              </TabContext>
            )}
          </WithLoader>
        </DrawerRight>
      </Box>
      {singleDevice && (
        <SPMActivityDialog
          open={activityDialogOpen}
          handleClose={handleActivityDialogClose}
          originalActivityEndDate={singleDevice.activity_end_date?.toString()}
          isActive={singleDevice.active}
          onSubmit={handleActivityDialogSubmit}
          spmObjectName={t`device`}
          isSubmitLoading={isSubmitLoading}
        />
      )}
    </Box>
  )
}
